import { Injectable, OnInit } from '@angular/core';
import { StorageService } from './storage.service';
import {AppService} from "../app.service";
import {SessionService} from "./session.service";
import { AppcuesService } from '../services/appcues.service';
import { HubspotService } from '../services/hubspot.service';
import {SentryService} from "../services/sentry.service";
import { RBACService } from './rbac.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnInit {

  constructor(
    private sessionService: SessionService,
    private app: AppService,
    public storage: StorageService,
    private appcuesService: AppcuesService,
    private hubspotService: HubspotService,
    private sentry: SentryService,
    private rbac: RBACService
  ) {}

  ngOnInit(): void {}

  /**
   * Updates the account authentication details based on the provided response.
   * This method sets the user's authentication status, stores authentication tokens,
   * applies user, account, and user-account link data, integrates tracking/setup with
   * external services like Sentry and Appcues.
   *
   * Similar code in sc-web-portal/src/app/admin/accounts/accounts.component.ts
   * Similar code in sc-web-portal/src/app/top-nav/top-nav.component.ts
   * Similar code in sc-web-portal/src/app/user/account-switcher/account-switcher.component.ts
   *
   * @param {Object} response - The response object containing authentication and user-related data.
   * @param {string} response.api_token - The authentication token for the user.
   * @param {Object} response.permissions - The permissions granted to the user.
   * @param {Object} response.user - The user details to be applied.
   * @param {Object} response.account - The account details to be applied.
   * @param {Object} response.user_account_link - The user-account link details to be applied.
   *
   * @return {void} Does not return any value.
   */
  updateAccountAuthDetails(response: any): void {
    // Set the user's authenticated status to true.
    this.app.isAuthenticated = true;

    // Store the user's auth token.
    this.storage.set('AuthToken', response.api_token);
    this.storage.set('Permissions', response.permissions);

    // Store the user, account and user account link data.
    this.app.user.apply(response.user);
    this.app.account.apply(response.account);
    this.app.userAccountLink.apply(response.user_account_link);

    // Apply permissions last: userCanAccess() also reads app.user.is_sitesoft_admin, and applying
    // permissions emits the change notification that components use to recompute their flags.
    this.rbac.applyPermissions(response.permissions);

    this.sentry.setUser(this.app.user.id);
    this.sentry.setActiveAccount(this.app.account.id);

    this.appcuesService.onIdentifyUser(this.app.user.email, {
      contact_person: this.app.user.contact_person,
      email: this.app.user.email,
      mobile: this.app.user.mobile,
      trade: this.app.user.trade,
      address: this.app.user.address,
      suburb: this.app.user.suburb,
      city: this.app.user.city,
      postal_code: this.app.user.postal_code,
      country: this.app.user.country
    });

    this.appcuesService.onIdentifyGroup(this.app.account.name, {
      name: this.app.account.name,
      industry: this.app.account.industry,
      address: this.app.account.address,
      suburb: this.app.account.suburb,
      city: this.app.account.city,
      postal_code: this.app.account.postal_code,
      country: this.app.account.country
    });

    // Load the HubSpot live chat widget and identify the logged in user so the
    // chat is associated with their name and email (only when a portal id is set).
    this.hubspotService.onIdentifyUser(this.app.user.email, {
      firstname: this.app.user.contact_person,
      phone: this.app.user.mobile,
      company: this.app.account.name,
      trade: this.app.user.trade,
      city: this.app.user.city,
      country: this.app.user.country
    });

    // Set quick access session if present (for super admin quick access timer).
    this.app.quickAccessSession = response.quick_access_session || null;
  }

  /**
   * Signs the user out of the application by resetting the authentication state,
   * user data, account data, and any related services.
   *
   * @param {string} [additionalMessage=''] Optional message to include during the signOut process.
   * @return {void} Does not return any value.
   */
  signOut(additionalMessage: string = ''): void {
    this.app.isAuthenticated = false;

    this.app.user.reset();
    this.app.account.reset();
    this.app.quickAccessSession = null;

    this.appcuesService.onReset();

    // Clear the HubSpot chat state so the next user starts a fresh session.
    this.hubspotService.onReset();

    this.sentry.setUser(null);
    this.sentry.setActiveAccount(null);

    // keep for backward compatability
    this.sessionService.signout(additionalMessage);

    // Clear the permissions.
    this.rbac.clearPermissions();
  }
}
